import { useMemo } from "react"

import { useTranslation } from "react-i18next"
import { Cell, Pie, PieChart } from "recharts"

import type { DashboardUseCase } from "../../types/use-case.types"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

import { STRATEGIC_ALIGNMENTS } from "../../types/use-case.types"

const SA_COLORS: Record<string, string> = {
  CE: "hsl(var(--chart-1))",
  OE: "hsl(var(--chart-3))",
  "R&D": "hsl(var(--chart-2))",
}

const SA_LABEL_MAP = Object.fromEntries(STRATEGIC_ALIGNMENTS.map((sa) => [sa.value, sa.label]))

type StrategicAlignmentDonutProps = {
  useCases: DashboardUseCase[]
}

export function StrategicAlignmentDonut({ useCases }: StrategicAlignmentDonutProps) {
  const { t } = useTranslation()

  const chartConfig = useMemo(
    () =>
      ({
        value: { label: t("useCases.dashboard.charts.count") },
      }) satisfies ChartConfig,
    [t]
  )

  const { data, total } = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const uc of useCases) {
      for (const val of uc.saSel) {
        counts[val] = (counts[val] || 0) + 1
      }
    }
    return {
      data: Object.entries(counts).map(([key, value]) => ({
        name: SA_LABEL_MAP[key] || key,
        value,
        color: SA_COLORS[key] || "hsl(var(--muted-foreground))",
      })),
      total: Object.values(counts).reduce((a, b) => a + b, 0),
    }
  }, [useCases])

  if (data.length === 0) return null

  return (
    <Card className="rounded-lg shadow-sm">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">
          {t("useCases.dashboard.charts.strategicAlignment")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-4">
          <ChartContainer className="h-[200px] w-[60%]" config={chartConfig}>
            <PieChart>
              <Pie
                cx="50%"
                cy="50%"
                data={data}
                dataKey="value"
                innerRadius={50}
                nameKey="name"
                outerRadius={80}
                strokeWidth={1}
              >
                {data.map((entry, i) => (
                  <Cell key={i} fill={entry.color} stroke="hsl(var(--foreground))" />
                ))}
              </Pie>
              <ChartTooltip content={<ChartTooltipContent hideLabel />} />
              <text
                dominantBaseline="middle"
                fill="hsl(var(--foreground))"
                fontSize={24}
                fontWeight="bold"
                textAnchor="middle"
                x="50%"
                y="50%"
              >
                {total}
              </text>
            </PieChart>
          </ChartContainer>
          <div className="flex flex-col gap-2">
            {data.map((entry) => (
              <div key={entry.name} className="flex items-center gap-2 text-sm">
                <div
                  className="border-foreground size-3 rounded-full border"
                  style={{ backgroundColor: entry.color }}
                />
                <span>
                  {entry.name} ({entry.value})
                </span>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
